/**
 * useMe / useRole / useCan — single source of truth for who the user is
 * and what they're allowed to do.
 *
 * Backed by /v1/me. RLS enforces server-side; this is the UX gating layer.
 *
 *   const me = useMe();                  // full profile
 *   const role = useRole();              // 'owner' | 'admin' | 'member' | 'viewer'
 *   const can = useCan();                // { manageSettings, approveActions, ... }
 *   if (!can.manageIntegrations) hide();
 */
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';

export type Role = 'owner' | 'admin' | 'member' | 'viewer';

export type PlatformType = 'sales' | 'finance' | 'operations' | 'marketing' | 'executive' | 'tax' | 'clients' | 'sow';

export interface MyDepartment {
  id: string;
  name: string;
  color: string | null;
  quarterlyRevenueGoalCents: number | null;
  weeklyExpenseCents: number | null;
  myRole: 'head' | 'member';
  platformType: PlatformType | null;
}

export interface SubscriptionInfo {
  plan: string;
  status: string;
  trialEndsAt: string | null;
  trialDaysRemaining: number | null;
  isActive: boolean;
}

export interface MeResponse {
  user: { id: string; email: string };
  account: { id: string; name?: string; slug?: string; created_at?: string };
  role: Role;
  subscription?: SubscriptionInfo;
  permissions: {
    manageIntegrations: boolean;
    manageTeam: boolean;
    manageSettings: boolean;
    manageBilling: boolean;
    manageDepartments: boolean;
    approveActions: boolean;
    viewFinancials: boolean;
    editTasks: boolean;
    exportData: boolean;
    manageOwnDepartment: boolean;
    canViewTeam: boolean;
    canViewOrg: boolean;
  };
  teamSize: number;
  departments: MyDepartment[];
  grantedScopes: string[];
  impersonating: { actorUserId: string; actorEmail: string } | null;
  /** Non-null = the owner scheduled this account for deletion; every other API call is blocked. */
  deletionPending: { scheduledFor: string } | null;
  assignedPlatform: PlatformType | null;
  /** Per-workspace product switches.
   *  tax / sow are separately-sold add-ons (accounts.tax_enabled / sow_enabled,
   *  enforced server-side). owner / sales / clients derive from `products`
   *  (owner implies the other two).
   *  partner is the INVERSE of an add-on: a design-partner workspace
   *  (accounts.partner_surface) does NOT see Intelligence, Marketing, Model
   *  Insights or the nerve center. Optional, so an API that predates it is fine. */
  features: { tax: boolean; sow: boolean; partner?: boolean; owner: boolean; sales: boolean; clients: boolean };
  /** Home product of the workspace. 'business' = Owner bundle with the owner
   *  dashboard. A standalone kind has no owner dashboard: everyone, owner
   *  included, is pinned to `homePath` (lib/platform-pin.ts).
   *  WorkspaceKind is a superset of main's 'business' | 'sow_desk' — it adds the
   *  two standalone product kinds, so nothing that narrowed to the old union breaks. */
  workspaceKind: WorkspaceKind;
  /** Where every member of this workspace lands. Absent on an API that predates it. */
  homePath?: string;
  /** Sellable products this workspace is entitled to. Absent on an API that predates it. */
  products?: ProductEntitlement[];
}

export type WorkspaceKind = 'business' | 'sales' | 'clients' | 'sow_desk';
export type ProductEntitlement = 'owner' | 'sales' | 'clients';

/**
 * Permissive default — used while /v1/me is loading OR if the call errored
 * for any reason. Showing too much UI is fine because the SERVER enforces
 * every permission via RLS + endpoint filters. Hiding Settings prematurely
 * is much worse: the user can't connect integrations and is dead in the
 * water with no recovery path.
 *
 * Once /v1/me responds, the real role + permissions take over.
 */
const PERMISSIVE_DEFAULT: MeResponse = {
  user: { id: '', email: '' },
  account: { id: '' },
  role: 'owner',
  permissions: {
    manageIntegrations: true, manageTeam: true, manageSettings: true, manageBilling: true, manageDepartments: true,
    approveActions: true, viewFinancials: true, editTasks: true, exportData: true,
    manageOwnDepartment: false,
    canViewTeam: true, canViewOrg: true,
  },
  teamSize: 1,
  departments: [],
  grantedScopes: [],
  impersonating: null,
  deletionPending: null,
  assignedPlatform: null,
  // Tax / SOW hidden by default — rare paid add-ons, and the API 403s anyway.
  // The sellable products default to the full Owner bundle (what every
  // business workspace has) so nothing is hidden while /v1/me loads.
  features: { tax: false, sow: false, owner: true, sales: true, clients: true },
  workspaceKind: 'business',
  homePath: '/dashboard',
  products: ['owner', 'sales', 'clients'],
};

async function fetchMe(): Promise<MeResponse> {
  return api<MeResponse>('/v1/me/'); // let React Query own the error state
}

export function useMe(): MeResponse {
  const { data } = useQuery({
    queryKey: ['me'],
    queryFn: fetchMe,
    staleTime: 5 * 60_000,
    retry: 2,
  });
  return data ?? PERMISSIVE_DEFAULT;
}

/** Use when you need to know whether the role data has actually arrived. */
export function useMeLoaded(): boolean {
  const { data } = useQuery({
    queryKey: ['me'],
    queryFn: fetchMe,
    staleTime: 5 * 60_000,
  });
  return !!data?.user.id;
}

export function useRole(): Role {
  return useMe().role;
}

export function useCan() {
  return useMe().permissions;
}

export const ROLE_LABEL: Record<Role, string> = {
  owner:  'Owner',
  admin:  'Admin',
  member: 'Member',
  viewer: 'Viewer',
};

export const ROLE_DESCRIPTION: Record<Role, string> = {
  owner:  'Full access including billing and team management',
  admin:  'Full access except billing',
  member: 'Day-to-day operations; cannot manage integrations or team',
  viewer: 'Read-only — can browse dashboards but not take actions',
};

const DEFAULT_SUBSCRIPTION: SubscriptionInfo = {
  plan: 'trial',
  status: 'trialing',
  trialEndsAt: null,
  trialDaysRemaining: null,
  isActive: true,
};

export function useSubscription(): SubscriptionInfo {
  return useMe().subscription ?? DEFAULT_SUBSCRIPTION;
}
